import { usePopup } from '../../../context/PopupProvider';
import { FC, useCallback } from 'react';
import { User } from '../../../types/User';
import { DefaultFooterComponent, PopupFooterProps, PopupStateProps, Radio, showAlert } from '@silverfort/sf-components';
import { useMatchUsers, UseMatchUsersParams } from '../../../api';

interface Params {
    onMatchedUsers: () => void;
}

interface MatchUsersPopupState {
    primaryUserGuid?: User['guid'];
}

type ContentProps = PopupStateProps<MatchUsersPopupState> & {
    users?: User[];
};

type FooterProps = PopupFooterProps<MatchUsersPopupState>;

const Content: FC<ContentProps> = ({ users, popupState, setPopupState }) => {
    const { primaryUserGuid } = popupState ?? {};

    const handleChange = (userGuid: User['guid'], selected: boolean) => {
        setPopupState({
            primaryUserGuid: selected ? userGuid : undefined
        });
    };

    return (
        <div className="flex flex-col gap-2">
            <span className="font-sf text-13 leading-[19px] tracking-[0.203px]">
                Select the primary to associate with matched users
            </span>
            <div className="flex flex-col rounded-[8px] border border-sf-gray-100">
                {users?.map((user) => (
                    <Radio
                        key={user.guid}
                        value={user.guid}
                        containerClassName="ps-2 font-sf h-12 border-b last:border-b-0 border-sf-gray-100"
                        title={user.userName}
                        checked={primaryUserGuid === user.guid}
                        onChange={(event) => handleChange(user.guid, event.target.checked)}
                    />
                ))}
            </div>
        </div>
    );
};

const Footer: FC<FooterProps> = ({ popupState, ...footerProps }) => {
    const { primaryUserGuid } = popupState ?? {};

    return (
        <DefaultFooterComponent
            primaryButtonProps={{
                text: 'Match',
                disabled: !primaryUserGuid
            }}
            {...footerProps}
        />
    );
};

const handleSuccess = () =>
    showAlert('Users are now matched. Token sharing  configuration is taken from your settings.', 'success', {
        title: 'Users are now matched'
    });

const handleError: UseMatchUsersParams['onError'] = (error) => {
    // todo: this is because usePost, should be fixed after implementing https://silverfort.atlassian.net/browse/AF-40652
    const isAlreadyMatchedUsersError =
        error.error ===
        'Cannot match users - some of the secondary users for the new matching are currently a primary user.';
    const errorMessage = isAlreadyMatchedUsersError
        ? 'These users are already matched. Unmatch them and try again.'
        : 'An error occurred when matching users.';

    showAlert(errorMessage, 'error', {
        title: 'Failed to match users'
    });
};

export const useMatchUsersPopup = ({ onMatchedUsers }: Params) => {
    const { openPopup, closePopup } = usePopup();
    const { matchUsers } = useMatchUsers({
        onSuccess: useCallback(() => {
            handleSuccess();
            onMatchedUsers();
        }, [onMatchedUsers]),
        onError: handleError
    });

    const openMatchUsersPopup = useCallback(
        (users: User[]) => {
            openPopup({
                title: 'Match users',
                popupClassName: 'w-[360px]',
                ContentComponent: Content,
                initialState: { primaryUserGuid: undefined },
                ContentComponentProps: { users },
                FooterComponent: Footer,
                onApprove: (state) => {
                    const { primaryUserGuid } = state ?? {};
                    const secondaryUsersGuids = users
                        .map((user) => user.guid)
                        .filter((guid) => guid !== primaryUserGuid);

                    if (primaryUserGuid) {
                        matchUsers(primaryUserGuid, secondaryUsersGuids);
                    }
                }
            });
        },
        [openPopup, closePopup]
    );

    return { openMatchUsersPopup };
};
